import React, { useEffect, useRef, useState } from "react";
import { ensurePdfJsLoaded } from "../utils/loadPdfJsCdn";

function resolvePdfUrl(src) {
  if (!src) return "";
  const withoutHash = src.split("#")[0];
  try {
    return new URL(withoutHash, window.location.href).href;
  } catch {
    return withoutHash;
  }
}

/**
 * Renders consent PDFs with PDF.js (canvas). Works when Firefox is set to
 * download PDFs instead of opening them in a frame (iframe would trigger download).
 */
const ConsentFormPdfViewer = ({ src, title = "Consent Form", height = 600 }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fallbackToIframe, setFallbackToIframe] = useState(false);

  useEffect(() => {
    if (!src) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const pdfUrl = resolvePdfUrl(src);

    const run = async () => {
      setLoading(true);
      setError(null);
      setFallbackToIframe(false);
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }

      try {
        const pdfjsLib = await ensurePdfJsLoaded();
        if (cancelled) return;

        const loadingTask = pdfjsLib.getDocument({ url: pdfUrl });
        const pdf = await loadingTask.promise;
        if (cancelled || !containerRef.current) return;

        containerRef.current.innerHTML = "";

        for (let p = 1; p <= pdf.numPages; p += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(p);
          const hostWidth = containerRef.current.clientWidth || 900;
          const baseViewport = page.getViewport({ scale: 1 });
          const fitScale = Math.max((hostWidth - 24) / baseViewport.width, 0.5);
          const outputScale = Math.min(window.devicePixelRatio || 1, 2);
          const viewport = page.getViewport({ scale: fitScale * outputScale });
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d", { alpha: false });
          if (!ctx) {
            throw new Error("Could not initialize canvas context");
          }
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          canvas.style.width = `${viewport.width / outputScale}px`;
          canvas.style.height = `${viewport.height / outputScale}px`;
          canvas.style.maxWidth = "100%";
          canvas.style.display = "block";
          canvas.style.margin = "0 auto 16px";
          canvas.style.boxShadow = "0 1px 3px rgba(0,0,0,0.2)";
          const renderTask = page.render({
            canvasContext: ctx,
            viewport,
          });
          await renderTask.promise;
          if (!cancelled && containerRef.current) {
            containerRef.current.appendChild(canvas);
          }
        }
      } catch (e) {
        if (!cancelled) {
          const message = e?.message || "";
          if (
            message.includes("Failed to fetch") ||
            message.includes("NetworkError") ||
            message.includes("CORS")
          ) {
            // Cross-origin PDFs may block fetch for PDF.js. Use iframe fallback.
            setFallbackToIframe(true);
          } else {
            setError(
              message ||
                "Unable to display this PDF. Check your connection or try again."
            );
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [src]);

  if (!src) {
    return null;
  }

  if (fallbackToIframe) {
    return (
      <iframe
        src={src}
        title={title}
        width="100%"
        height={`${height}px`}
        style={{ border: "1px solid #ddd" }}
      />
    );
  }

  return (
    <div style={{ width: "100%", position: "relative" }}>
      {loading ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.88)",
            zIndex: 1,
            border: "1px solid #ddd",
            borderRadius: 2,
            minHeight: Math.min(height, 120),
          }}
        >
          <span style={{ color: "#555" }}>Loading document...</span>
        </div>
      ) : null}
      {error ? (
        <div style={{ padding: 12, color: "#c0392b" }}>{error}</div>
      ) : null}
      <div
        ref={containerRef}
        style={{
          maxHeight: height,
          overflowY: "auto",
          overflowX: "auto",
          border: "1px solid #ddd",
          background: "#525659",
          padding: "12px 8px",
          display: error ? "none" : "block",
          width: "100%",
          boxSizing: "border-box",
          minHeight: loading ? Math.min(height, 200) : undefined,
        }}
        role="region"
        aria-label={title}
        aria-busy={loading}
      />
    </div>
  );
};

export default ConsentFormPdfViewer;
